import { useEffect, useState } from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { Search, X } from 'lucide-react';
import type { ResumoOrcamentosResponse } from '@triade/contracts';
import { useCliente, useClientes } from '@/api/clientes';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useDebounced } from '@/hooks/use-debounced';
import { formatarContagem, formatarData } from '@/lib/format';
import { cn } from '@/lib/utils';
import { type BuscaLista, type StatusOrcamento } from './tipos';

const ABAS: Array<{ status: StatusOrcamento | undefined; rotulo: string }> = [
  { status: undefined, rotulo: 'Todos' },
  { status: 'RASCUNHO', rotulo: 'Rascunho' },
  { status: 'ENVIADO', rotulo: 'Enviados' },
  { status: 'EM_NEGOCIACAO', rotulo: 'Em negociação' },
  { status: 'APROVADO', rotulo: 'Aprovados' },
  { status: 'PERDIDO', rotulo: 'Perdidos' },
];

const CHIP = 'flex h-[34px] items-center gap-1.5 rounded-[9px] border border-dashed border-input bg-card px-3 text-small text-ink-body hover:bg-line-soft';
const CHIP_ATIVO = 'border-solid border-primary bg-card';

export interface FiltrosListaProps {
  busca: BuscaLista;
  onBusca: (mudanca: Partial<BuscaLista>) => void;
  /** Contagens por status (da API) para as abas; undefined enquanto carrega. */
  resumo?: ResumoOrcamentosResponse | undefined;
  /** Mostra as abas de status (a lista usa; o kanban tem uma coluna por status). */
  comAbas?: boolean;
}

/** Abas de status, busca (numero ou cliente) e filtros de cliente e periodo. Nada filtra no front: muda a URL e a API responde. */
export function FiltrosLista({ busca, onBusca, resumo, comAbas = true }: FiltrosListaProps) {
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtraso = useDebounced(termo.trim(), 300);
  // Termo digitado -> URL (debounce); URL alterada por fora (limpar filtros) -> campo.
  useEffect(() => {
    if (termoAtraso !== (busca.q ?? '')) onBusca({ q: termoAtraso || undefined, pagina: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtraso]);
  useEffect(() => setTermo(busca.q ?? ''), [busca.q]);

  const quantidade = (s: StatusOrcamento | undefined) => (resumo ? (s ? resumo.porStatus[s].quantidade : resumo.geral.quantidade) : undefined);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
      {comAbas ? (
        <div role="tablist" aria-label="Situação" className="flex flex-wrap gap-1 rounded-[10px] bg-line-soft p-[3px]">
          {ABAS.map((a) => {
            const ativa = busca.status === a.status;
            const n = quantidade(a.status);
            return (
              <button
                key={a.rotulo}
                type="button"
                role="tab"
                aria-selected={ativa}
                onClick={() => onBusca({ status: a.status, pagina: undefined })}
                className={cn('h-[30px] rounded-lg px-3 text-small', ativa ? 'bg-card font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.08)]' : 'text-ink-secondary hover:text-ink')}
              >
                {a.rotulo}
                {n !== undefined && <span className="tabular ml-1.5 text-caption text-ink-muted">{formatarContagem(n)}</span>}
              </button>
            );
          })}
        </div>
      ) : (
        <span />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex h-[34px] w-[240px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
          <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
          <label htmlFor="orc-q" className="sr-only">
            Buscar orçamento
          </label>
          <input
            id="orc-q"
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Número ou cliente"
            autoComplete="off"
            className="w-full min-w-0 bg-transparent text-small outline-none"
          />
        </div>
        <button
          type="button"
          aria-pressed={busca.vencido === true}
          onClick={() => onBusca({ vencido: busca.vencido ? undefined : true, pagina: undefined })}
          className={cn(CHIP, busca.vencido && CHIP_ATIVO)}
        >
          {busca.vencido ? 'Só vencidos' : '+ Vencidos'}
        </button>
        {busca.vencendo !== undefined && (
          <span data-testid="chip-vencendo" className={cn(CHIP, CHIP_ATIVO)}>
            {`Vencendo em ${busca.vencendo} ${busca.vencendo === 1 ? 'dia' : 'dias'}`}
            <button
              type="button"
              aria-label="Remover filtro de vencimento"
              onClick={() => onBusca({ vencendo: undefined, pagina: undefined })}
              className="rounded text-ink-muted hover:text-ink"
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          </span>
        )}
        <FiltroCliente clienteId={busca.clienteId} onChange={(id) => onBusca({ clienteId: id, pagina: undefined })} />
        <FiltroPeriodo de={busca.dataDe} ate={busca.dataAte} onChange={(de, ate) => onBusca({ dataDe: de, dataAte: ate, pagina: undefined })} />
      </div>
    </div>
  );
}

function FiltroCliente({ clienteId, onChange }: { clienteId: string | undefined; onChange: (id: string | undefined) => void }) {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const busca = useDebounced(termo.trim(), 250);
  const clientes = useClientes({ q: busca || undefined, limit: 20 });
  const atual = useCliente(clienteId);
  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Trigger asChild>
        <button type="button" aria-haspopup="dialog" className={cn(CHIP, clienteId && CHIP_ATIVO)}>
          {clienteId ? `Cliente: ${atual.data?.nome ?? '…'}` : '+ Cliente'}
          {clienteId && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Remover filtro de cliente"
              onClick={(e) => {
                e.stopPropagation();
                onChange(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(undefined);
                }
              }}
              className="rounded text-ink-muted hover:text-ink"
            >
              <X aria-hidden="true" className="size-3.5" />
            </span>
          )}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align="end" sideOffset={6} className="z-50 w-[320px] overflow-hidden rounded-xl border border-border bg-popover shadow-menu">
          <Command shouldFilter={false} label="Filtrar por cliente">
            <CommandInput placeholder="Nome ou documento" value={termo} onValueChange={setTermo} />
            <CommandList aria-label="Clientes">
              {clientes.error ? (
                <div role="alert" className="px-3 py-4 text-small text-danger-ink">
                  Não foi possível buscar clientes: {clientes.error.message}
                </div>
              ) : clientes.isPending ? (
                <div role="status" className="px-3 py-4 text-small text-ink-muted">
                  Buscando…
                </div>
              ) : (
                <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
              )}
              {(clientes.data ?? []).map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => {
                    onChange(c.id);
                    setAberto(false);
                  }}
                >
                  {c.nome}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function FiltroPeriodo({ de, ate, onChange }: { de: string | undefined; ate: string | undefined; onChange: (de: string | undefined, ate: string | undefined) => void }) {
  const ativo = Boolean(de || ate);
  const rotulo = ativo ? `Período: ${de ? formatarData(de) : '…'} – ${ate ? formatarData(ate) : '…'}` : '+ Período';
  const campo = 'h-9 w-full rounded-[10px] border border-input bg-card px-2.5 text-small outline-none focus-visible:border-primary focus-visible:shadow-focus';
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button type="button" aria-haspopup="dialog" className={cn(CHIP, ativo && CHIP_ATIVO)}>
          {rotulo}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align="end" sideOffset={6} className="z-50 w-[280px] rounded-xl border border-border bg-popover p-3 shadow-menu">
          <div className="grid gap-2.5">
            <label className="text-caption font-medium text-ink-secondary">
              De
              <input type="date" aria-label="Data inicial" value={de ?? ''} max={ate} onChange={(e) => onChange(e.target.value || undefined, ate)} className={`${campo} mt-1`} />
            </label>
            <label className="text-caption font-medium text-ink-secondary">
              Até
              <input type="date" aria-label="Data final" value={ate ?? ''} min={de} onChange={(e) => onChange(de, e.target.value || undefined)} className={`${campo} mt-1`} />
            </label>
            {ativo && (
              <button type="button" onClick={() => onChange(undefined, undefined)} className="h-8 rounded-lg border border-border text-small hover:bg-line-soft">
                Limpar período
              </button>
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
